<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="row m-4 mt-0">
      <div class="col-sm-12">

        <div v-if="lastResult" class="alert alert-success d-flex justify-content-between align-items-start py-2 px-3 mb-3">
          <div>
            <i class="fa fa-circle-check me-2"></i>
            {{ helper.trans('rates_management.last_operation', {changed: lastResult.summary.changed, traders: lastResult.summary.tradersChanged}) }}
            <div class="small text-muted mt-1">
              {{ helper.trans('rates_management.last_operation_id') }}
              <code>{{ lastResult.operationId }}</code>
            </div>
          </div>
          <button type="button" class="btn-close" @click="lastResult = null"></button>
        </div>

        <div class="card card-body mb-3">
          <h6 class="text-capitalize-first mb-3">{{ helper.trans('rates_management.selection') }}</h6>

          <div class="row g-3">
            <div class="col-md-12 col-lg-2">
              <label class="form-label text-capitalize-first">{{ helper.trans('rates_management.direction') }}</label>
              <div>
                <div class="btn-group btn-group-sm w-100" role="group">
                  <input type="radio" class="btn-check" id="rates-direction-in" :value="Direction.PAY_IN" v-model="form.direction">
                  <label class="btn btn-outline-primary" for="rates-direction-in">{{ helper.trans('rates_management.direction_in') }}</label>
                  <input type="radio" class="btn-check" id="rates-direction-out" :value="Direction.PAY_OUT" v-model="form.direction">
                  <label class="btn btn-outline-primary" for="rates-direction-out">{{ helper.trans('rates_management.direction_out') }}</label>
                </div>
              </div>
            </div>

            <div class="col-md-12 col-lg-4">
              <label class="form-label text-capitalize-first">{{ helper.trans('rates_management.traffic_groups') }}</label>
              <TrafficGroupMultiselect :multiple="true" small :direction="form.direction" v-model="form.trafficGroups"></TrafficGroupMultiselect>
            </div>

            <div class="col-md-6 col-lg-3">
              <label class="form-label text-capitalize-first">{{ helper.trans('rates_management.team_leads') }}</label>
              <TeamLeadMultiselect :multiple="true" v-model="form.teamLeads"></TeamLeadMultiselect>
            </div>

            <div class="col-md-6 col-lg-3">
              <label class="form-label text-capitalize-first">{{ helper.trans('rates_management.traders') }}</label>
              <TraderMultiselect :multiple="true" v-model="form.traders"></TraderMultiselect>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="rates-include-disabled" v-model="form.includeDisabled">
                <label class="form-check-label" for="rates-include-disabled">
                  {{ helper.trans('rates_management.include_disabled') }}
                  <span class="text-muted small ms-1">{{ helper.trans('rates_management.include_disabled_hint') }}</span>
                </label>
              </div>
              <div class="small text-muted mt-2">
                <i class="fa fa-circle-info me-1"></i>{{ helper.trans('rates_management.filters_hint') }}
              </div>
            </div>
          </div>
        </div>

        <div class="card card-body">
          <h6 class="text-capitalize-first mb-3">{{ helper.trans('rates_management.change') }}</h6>

          <div class="row g-3">
            <div class="col-md-12 col-lg-4">
              <label class="form-label text-capitalize-first">{{ helper.trans('rates_management.target') }}</label>
              <div>
                <div class="btn-group btn-group-sm w-100" role="group">
                  <input type="radio" class="btn-check" id="rates-target-fee" :value="TraderRatesBulkTarget.TRADER_FEE" v-model="form.target">
                  <label class="btn btn-outline-primary text-nowrap" for="rates-target-fee">{{ helper.trans('rates_management.target_trader_fee') }}</label>
                  <input type="radio" class="btn-check" id="rates-target-cut" :value="TraderRatesBulkTarget.TEAM_LEAD_CUT" v-model="form.target">
                  <label class="btn btn-outline-primary text-nowrap" for="rates-target-cut">{{ helper.trans('rates_management.target_team_lead_cut') }}</label>
                </div>
              </div>
            </div>

            <div class="col-md-6 col-lg-3">
              <label class="form-label text-capitalize-first">{{ helper.trans('rates_management.mode') }}</label>
              <select v-model="form.mode" class="form-select form-select-sm">
                <option value="">{{ helper.trans('rates_management.select_mode') }}</option>
                <option v-for="mode in modes" :key="mode.value" :value="mode.value">{{ helper.trans(mode.label) }}</option>
              </select>
            </div>

            <div class="col-md-6 col-lg-2">
              <label class="form-label text-capitalize-first">{{ helper.trans('rates_management.value') }}</label>
              <input type="number" step="0.001" :min="valueMin" max="99.999" v-model="form.value" class="form-control form-control-sm" placeholder="0.5">
            </div>

            <div class="col-md-12 col-lg-3 d-flex align-items-end justify-content-lg-end text-nowrap">
              <button type="button" class="btn btn-outline-secondary btn-sm rounded-pill text-capitalize-first me-2" @click="reset()">{{ helper.trans('rates_management.reset') }}</button>
              <button type="button" class="btn btn-primary btn-sm rounded-pill text-capitalize-first px-4" @click="save()">{{ helper.trans('rates_management.save') }}</button>
            </div>
          </div>

          <div v-if="form.target === TraderRatesBulkTarget.TEAM_LEAD_CUT" class="small text-muted mt-3">
            <i class="fa fa-circle-info me-1"></i>{{ helper.trans('rates_management.target_team_lead_cut_hint') }}
          </div>
          <div class="small text-muted mt-2">
            <i class="fa fa-shield-halved me-1"></i>{{ helper.trans('rates_management.save_hint') }}
          </div>
        </div>

      </div>
    </div>

    <TraderRatesBulkReportModal
      v-model="reportModal"
      :report="report"
      :request="submittedRequest"
      :drift="drift"
      :applying="applying"
      @confirm="confirm()"
    ></TraderRatesBulkReportModal>
  </LoaderWrapper>
</template>

<script lang="ts">
import {computed, defineComponent, reactive, ref, toRefs, watch} from 'vue'
import {helper} from "@/service/CommonHelper";
import {api} from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import TrafficGroupMultiselect from "@/components/TrafficGroupMultiselect.vue";
import TeamLeadMultiselect from "@/components/TeamLeadMultiselect.vue";
import TraderMultiselect from "@/components/TraderMultiselect.vue";
import TraderRatesBulkReportModal from "@/components/TraderRatesBulkReportModal.vue";
import {Direction} from "@/models/enum/Direction";
import {RateChangeMode} from "@/models/enum/RateChangeMode";
import {TraderRatesBulkTarget} from "@/models/enum/TraderRatesBulkTarget";
import TraderRatesBulkRequestDTO from "@/models/request/TraderRatesBulkRequestDTO";
import TraderRatesBulkReportDTO from "@/models/TraderRatesBulkReportDTO";
import TraderRatesBulkApplyResultDTO from "@/models/TraderRatesBulkApplyResultDTO";
import TrafficGroupDTO from "@/models/TrafficGroupDTO";
import UserDTO from "@/models/UserDTO";
import TraderDTO from "@/models/TraderDTO";

const notification = useNotification()

const VALUE_PATTERN = /^-?\d{1,2}(\.\d{1,3})?$/
const MAX_VALUE = 99.999
const ABSOLUTE_MODES = [RateChangeMode.RATE_UP_UNTIL, RateChangeMode.RATE_DOWN_UNTIL, RateChangeMode.RATE_SET_ALL]

interface FormData {
  direction: Direction
  trafficGroups: TrafficGroupDTO[]
  teamLeads: UserDTO[]
  traders: TraderDTO[]
  includeDisabled: boolean
  target: TraderRatesBulkTarget
  mode: RateChangeMode | ''
  value: string
}

const emptyForm = (): FormData => ({
  direction: Direction.PAY_IN,
  trafficGroups: [],
  teamLeads: [],
  traders: [],
  includeDisabled: false,
  target: TraderRatesBulkTarget.TRADER_FEE,
  mode: '',
  value: '',
})

export default defineComponent({
  name: 'RatesManagement',
  components: {
    LoaderWrapper,
    TrafficGroupMultiselect,
    TeamLeadMultiselect,
    TraderMultiselect,
    TraderRatesBulkReportModal,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const form = reactive(emptyForm())

    const state = reactive({
      report: null as TraderRatesBulkReportDTO | null,
      submittedRequest: null as TraderRatesBulkRequestDTO | null,
      reportModal: false,
      drift: false,
      applying: false,
      lastResult: null as TraderRatesBulkApplyResultDTO | null,
    })

    const modes = [
      {value: RateChangeMode.RATE_UP_ON, label: 'rate_change_modes.rate_up_on'},
      {value: RateChangeMode.RATE_DOWN_ON, label: 'rate_change_modes.rate_down_on'},
      {value: RateChangeMode.RATE_UP_UNTIL, label: 'rate_change_modes.rate_up_until'},
      {value: RateChangeMode.RATE_DOWN_UNTIL, label: 'rate_change_modes.rate_down_until'},
      {value: RateChangeMode.RATE_SET_ALL, label: 'rate_change_modes.rate_set_all'},
    ]

    const valueMin = computed((): number => {
      const allowsNegative = form.direction === Direction.PAY_OUT
        && form.target === TraderRatesBulkTarget.TRADER_FEE
        && form.mode !== ''
        && ABSOLUTE_MODES.includes(form.mode)

      return allowsNegative ? -99 : 0
    })

    watch(() => form.direction, () => {
      form.trafficGroups = []
    })

    const fail = (text: string): void => {
      notification.notify({type: 'error', title: helper.trans('error'), text})
    }

    const validate = (): boolean => {
      if (form.trafficGroups.length === 0 && form.teamLeads.length === 0 && form.traders.length === 0) {
        fail(helper.trans('rates_management.error.filter_required'))
        return false
      }

      if (form.target === TraderRatesBulkTarget.TEAM_LEAD_CUT && form.teamLeads.length !== 1) {
        fail(helper.trans('rates_management.error.team_lead_required'))
        return false
      }

      if (form.mode === '') {
        fail(helper.trans('rates_management.error.mode_required'))
        return false
      }

      const value = String(form.value).trim()
      if (!VALUE_PATTERN.test(value) || parseFloat(value) < valueMin.value || parseFloat(value) > MAX_VALUE) {
        fail(helper.trans('rates_management.error.value_required', {min: valueMin.value}))
        return false
      }

      return true
    }

    const buildRequest = (): TraderRatesBulkRequestDTO => ({
      direction: form.direction,
      trafficGroupIds: form.trafficGroups.map(group => group.id),
      traderIds: form.traders.map(trader => trader.id),
      teamLeadIds: form.teamLeads.map(teamLead => teamLead.id),
      includeDisabled: form.includeDisabled,
      target: form.target,
      mode: form.mode as RateChangeMode,
      value: String(form.value).trim(),
    })

    const errorText = (error: any, fallback: string): string => {
      const violations = error?.response?.data
      return Array.isArray(violations) && violations[0]?.message ? violations[0].message : fallback
    }

    const save = async (): Promise<void> => {
      if (!validate()) {
        return
      }

      const request = buildRequest()
      loader.value?.show()
      try {
        state.report = await api.call.traderRatesBulk.preview(request)
        state.submittedRequest = request
        state.drift = false
        state.reportModal = true
      } catch (error: any) {
        fail(errorText(error, helper.trans('rates_management.error.preview_failed')))
      } finally {
        loader.value?.hide()
      }
    }

    const confirm = async (): Promise<void> => {
      if (state.report === null || state.submittedRequest === null || state.applying) {
        return
      }

      state.applying = true
      try {
        const response = await api.call.traderRatesBulk.apply(state.submittedRequest, state.report.previewHash)

        if (response.result !== null) {
          state.lastResult = response.result
          state.reportModal = false
          state.report = null
          state.drift = false
          notification.notify({type: 'success', title: helper.trans('success'), text: helper.trans('rates_management.last_operation', {changed: response.result.summary.changed, traders: response.result.summary.tradersChanged})})
          return
        }

        if (response.conflict.reason === 'rates_changed' && response.conflict.report !== null) {
          state.report = response.conflict.report
          state.drift = true
          return
        }

        notification.notify({type: 'warn', title: helper.trans('error'), text: helper.trans('rates_management.error.busy')})
      } catch (error: any) {
        fail(errorText(error, helper.trans('rates_management.error.apply_failed')))
      } finally {
        state.applying = false
      }
    }

    const reset = (): void => {
      Object.assign(form, emptyForm())
    }

    return {
      ...toRefs(state),
      loader,
      form,
      modes,
      valueMin,
      Direction,
      TraderRatesBulkTarget,
      helper,
      save,
      confirm,
      reset,
    }
  }
})
</script>
